import React, { FC } from 'react'
import classNames from 'classnames'
import { makeStyles } from 'transfix-ui/core'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { Tag } from 'apps/fleet-planner/types/apiTypes'
import { LoadsInsightsLabel } from './LoadsInsightsLabel'

interface IInsightsTagsProps {
  tags: Tag[]
  variant: string
  /**
   * @default false
   */
  isMobile?: boolean
  withLabel?: boolean
}

interface IVariantType {
  isFmsMarketInsightsFcfsLabelEnabled: boolean
  isFmsMarketInsightsPopularShipmentsLabel: boolean
  isFmsMarketInsightsGreatRateLabel: boolean
  tags: Tag[]
  variant: string
}

const getVariantType = ({
  isFmsMarketInsightsFcfsLabelEnabled,
  isFmsMarketInsightsGreatRateLabel,
  isFmsMarketInsightsPopularShipmentsLabel,
  tags,
  variant,
}: IVariantType) => {
  const variantType = [] // a load can have multiple labels at the same time

  if (isFmsMarketInsightsFcfsLabelEnabled) {
    variantType.push(variant)
  }

  if (isFmsMarketInsightsPopularShipmentsLabel) {
    // going through all the tags to find popular_shipment
    variantType.push(tags.some(tag => tag.variant === 'popular_shipment') ? 'popular_shipment' : '')
  }

  if (isFmsMarketInsightsGreatRateLabel) {
    // going through all the tags to find great_rate_shipment
    variantType.push(
      tags.some(tag => tag.variant === 'great_rate_shipment') ? 'great_rate_shipment' : ''
    )
  }

  return variantType
}

const useInsightsTagsStyles = makeStyles(theme => ({
  insightsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 5,
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
}))

export const InsightsTags: FC<IInsightsTagsProps> = ({
  tags = [],
  variant = '',
  isMobile = false,
  withLabel = false,
}) => {
  const classes = useInsightsTagsStyles()

  const isFmsMarketInsightsGreatRateLabel = useFeatureFlag('fmsMarketInsightsGreatRateLabel')
  const isFmsMarketInsightsFcfsLabelEnabled = useFeatureFlag('fmsMarketInsightsFcfsLabel')
  const isFmsMarketInsightsPopularShipmentsLabel = useFeatureFlag(
    'fmsMarketInsightsPopularShipmentsLabel'
  )

  const variantType = getVariantType({
    isFmsMarketInsightsFcfsLabelEnabled,
    isFmsMarketInsightsPopularShipmentsLabel,
    isFmsMarketInsightsGreatRateLabel,
    variant,
    tags,
  })
  const insightsContainer = classNames({
    [classes.insightsContainer]: variantType?.length > 1 && withLabel,
  })

  return (
    <div className={insightsContainer}>
      {variantType.map(variant => (
        <LoadsInsightsLabel
          key={variant}
          isMobile={isMobile}
          withLabel={withLabel}
          variant={variant || ''}
        />
      ))}
    </div>
  )
}
